import { AutoAwesome, Close, OpenInNew, Search, Tune } from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Fab,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { PerfilPresente } from 'config/perfisPresentes';
import { trackClick, usePageView } from 'utils/analytics';
import { BUILD_PROFILE } from 'utils/buildProfile';
import {
  FAIXAS_PRECO,
  PERFIL_OFERTAS_DIA,
  PERFIL_OFERTAS_SEMANA,
  PERFIL_TODOS,
  useIdeiasPresentesRules,
} from './IdeiasPresentesPage.rules';

const formatarPreco = (valor: number) =>
  valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const IdeiasPresentesPage: React.FC = () => {
  usePageView('IdeiasPresentes');

  const brandTheme = BUILD_PROFILE.theme;
  const {
    perfis,
    temOfertasDia,
    temOfertasSemana,
    afiliados,
    fontesSelecionadas,
    perfilSelecionado,
    produtos,
    total,
    carregando,
    erro,
    temMais,
    faixaSlug,
    busca,
    setBusca,
    carregarMais,
    selecionarPerfil,
    aplicarFiltros,
  } = useIdeiasPresentesRules();

  // Ordem fixa dos chips de categoria: Todos, Ofertas do dia/semana e o resto.
  const chipsPerfis = (() => {
    const lista: PerfilPresente[] = [];
    const add = (p?: PerfilPresente | null) => {
      if (p && !lista.some((x) => x.slug === p.slug)) lista.push(p);
    };
    add(PERFIL_TODOS);
    if (temOfertasDia) add(PERFIL_OFERTAS_DIA);
    if (temOfertasSemana) add(PERFIL_OFERTAS_SEMANA);
    perfis.forEach(add);
    return lista;
  })();

  // ----- Painel de filtros (drawer) com estado em rascunho, aplicado no "Aplicar" -----
  const [filtrosAberto, setFiltrosAberto] = useState(false);
  const [draftPerfil, setDraftPerfil] = useState<string>('todos');
  const [draftFontes, setDraftFontes] = useState<string[]>([]);
  const [draftFaixa, setDraftFaixa] = useState<string>('todas');

  const abrirFiltros = () => {
    setDraftPerfil(perfilSelecionado?.slug ?? 'todos');
    setDraftFontes(fontesSelecionadas);
    setDraftFaixa(faixaSlug);
    setFiltrosAberto(true);
    trackClick('ideias_filtros_abrir');
  };

  const alternarDraftFonte = (chave: string) =>
    setDraftFontes((atuais) =>
      atuais.includes(chave) ? atuais.filter((c) => c !== chave) : [...atuais, chave]
    );

  const limparDraft = () => {
    setDraftPerfil('todos');
    setDraftFontes([]);
    setDraftFaixa('todas');
  };

  const aplicar = () => {
    aplicarFiltros({ perfilSlug: draftPerfil, fontes: draftFontes, faixaSlug: draftFaixa });
    setFiltrosAberto(false);
    trackClick('ideias_filtros_aplicar', {
      perfil: draftPerfil,
      fontes: draftFontes.join(',') || 'todas',
      faixa: draftFaixa,
    });
  };

  // Filtros aplicados (para a barra "Ativos" e o contador do botão)
  const perfilAtivo = perfilSelecionado && perfilSelecionado.slug !== PERFIL_TODOS.slug;
  const faixaAtiva = FAIXAS_PRECO.find((f) => f.slug === faixaSlug && f.slug !== 'todas');
  const filtrosAtivosCount =
    (perfilAtivo ? 1 : 0) + fontesSelecionadas.length + (faixaAtiva ? 1 : 0);

  const fonteLabel = (chave: string) => afiliados.find((a) => a.chave === chave)?.label ?? chave;

  // Carrega mais ao chegar perto do fim da página (infinite scroll)
  const sentinelaRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelaRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) carregarMais();
      },
      { rootMargin: '700px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [carregarMais, perfilSelecionado]);

  // Card de perfil usado na landing (sem perfil selecionado)
  const renderPerfilCard = (perfil: PerfilPresente, onSelect: () => void) => (
    <Card
      key={perfil.slug}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${brandTheme.palette.borderSoft}`,
        background: `linear-gradient(150deg, rgba(255,255,255,0.94) 0%, ${brandTheme.palette.heroGradientEnd} 130%)`,
        transition: 'transform 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: `0 12px 24px ${brandTheme.palette.heroGlow}`,
        },
      }}
    >
      <CardActionArea
        onClick={onSelect}
        sx={{ p: { xs: 1.5, md: 2 }, textAlign: 'center', minHeight: { xs: 104, md: 128 } }}
      >
        <Typography sx={{ fontSize: { xs: '1.9rem', md: '2.3rem' }, lineHeight: 1 }}>
          {perfil.emoji}
        </Typography>
        <Typography sx={{ fontWeight: 700, mt: 0.75, fontSize: { xs: '0.92rem', md: '1rem' } }}>
          {perfil.label}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: { xs: 'none', sm: 'block' }, mt: 0.25 }}
        >
          {perfil.descricao}
        </Typography>
      </CardActionArea>
    </Card>
  );

  // Bloco de seção dentro do painel de filtros
  const renderSecaoFiltro = (
    titulo: string,
    subtitulo: string,
    conteudo: React.ReactNode
  ) => (
    <Box>
      <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>{titulo}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
        {subtitulo}
      </Typography>
      {conteudo}
    </Box>
  );

  // ----- Painel lateral de filtros -----
  const renderPainelFiltros = () => (
    <Drawer
      anchor="right"
      open={filtrosAberto}
      onClose={() => setFiltrosAberto(false)}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 460 },
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: brandTheme.palette.surface,
        },
      }}
    >
      {/* Cabeçalho do painel */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 2.5 },
          pb: 1.5,
          borderBottom: `1px solid ${brandTheme.palette.borderSoft}`,
        }}
      >
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Filtros
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Refine sua busca por categoria, loja e faixa de preço.
            </Typography>
          </Box>
          <IconButton aria-label="Fechar filtros" onClick={() => setFiltrosAberto(false)}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* Conteúdo rolável */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: { xs: 2, sm: 3 },
          py: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {renderSecaoFiltro(
          'Categoria',
          'Para quem é o presente?',
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {chipsPerfis.map((perfil) => {
              const ativo = perfil.slug === draftPerfil;
              return (
                <Chip
                  key={perfil.slug}
                  label={`${perfil.emoji} ${perfil.label}`}
                  onClick={() => setDraftPerfil(perfil.slug)}
                  color={ativo ? 'primary' : 'default'}
                  variant={ativo ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600, borderRadius: 999 }}
                />
              );
            })}
          </Box>
        )}

        {renderSecaoFiltro(
          'Loja',
          'Onde comprar',
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              icon={<AutoAwesome sx={{ fontSize: 18 }} />}
              label="Todas as lojas"
              onClick={() => setDraftFontes([])}
              color={draftFontes.length === 0 ? 'primary' : 'default'}
              variant={draftFontes.length === 0 ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, borderRadius: 999 }}
            />
            {afiliados.map((afiliado) => {
              const ativo = draftFontes.includes(afiliado.chave);
              return (
                <Chip
                  key={afiliado.chave}
                  avatar={
                    <Avatar
                      variant="rounded"
                      src={afiliado.logo}
                      alt={afiliado.label}
                      sx={{
                        bgcolor: afiliado.cor,
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        '& img': { objectFit: 'cover' },
                      }}
                    >
                      {afiliado.label.charAt(0)}
                    </Avatar>
                  }
                  label={afiliado.label}
                  onClick={() => alternarDraftFonte(afiliado.chave)}
                  color={ativo ? 'primary' : 'default'}
                  variant={ativo ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600, borderRadius: 999 }}
                />
              );
            })}
          </Box>
        )}

        {renderSecaoFiltro(
          'Faixa de preço',
          'Quanto pretende gastar?',
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {FAIXAS_PRECO.map((faixa) => {
              const ativo = faixa.slug === draftFaixa;
              return (
                <Chip
                  key={faixa.slug}
                  label={faixa.label}
                  onClick={() => setDraftFaixa(faixa.slug)}
                  color={ativo ? 'secondary' : 'default'}
                  variant={ativo ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600, borderRadius: 999 }}
                />
              );
            })}
          </Box>
        )}
      </Box>

      {/* Rodapé fixo */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          borderTop: `1px solid ${brandTheme.palette.borderSoft}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          backgroundColor: brandTheme.palette.surface,
        }}
      >
        <Button onClick={limparDraft} startIcon={<Close />} sx={{ fontWeight: 700, textTransform: 'none' }}>
          Limpar
        </Button>
        <Button
          variant="contained"
          onClick={aplicar}
          sx={{ borderRadius: 999, fontWeight: 800, textTransform: 'none', px: 3 }}
        >
          Aplicar filtros
        </Button>
      </Box>
    </Drawer>
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={1.5}
      width="100%"
      sx={{ mt: { xs: -2.5, md: -1.5 } }}
    >
      {!perfilSelecionado ? (
        // ----- Seleção de perfil (landing) -----
        <>
          <Box textAlign="center" maxWidth={680} px={1}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontSize: { xs: '1.45rem', md: '2.1rem' }, fontWeight: 800, lineHeight: 1.1 }}
            >
              💡 Ideias de Presentes
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 0.5, fontSize: { xs: '0.92rem', md: '1.02rem' } }}
            >
              Não sabe o que dar? Escolha para quem é o presente e a gente te inspira.
            </Typography>
          </Box>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }}
            gap={{ xs: 1.25, md: 1.75 }}
            width="100%"
            maxWidth={920}
          >
            {perfis.map((perfil) =>
              renderPerfilCard(perfil, () => {
                trackClick('ideias_perfil_card', { perfil: perfil.slug });
                selecionarPerfil(perfil.slug);
              })
            )}
          </Box>
        </>
      ) : (
        // ----- Lista de produtos do perfil -----
        <Box width="100%" maxWidth={1060} display="flex" flexDirection="column" gap={1.75}>
          {/* Cabeçalho: título (o botão de filtros é flutuante, ver FAB abaixo) */}
          <Typography
            component="h1"
            sx={{ fontWeight: 800, fontSize: { xs: '1.3rem', md: '1.6rem' }, lineHeight: 1.1 }}
          >
            💡 Ideias de Presentes
          </Typography>

          {/* Barra de filtros ativos */}
          {filtrosAtivosCount > 0 && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flexWrap: 'wrap', rowGap: 1 }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 800, letterSpacing: 0.6, color: 'text.secondary' }}
              >
                ATIVOS:
              </Typography>
              {perfilAtivo && perfilSelecionado && (
                <Chip
                  label={`${perfilSelecionado.emoji} ${perfilSelecionado.label}`}
                  onDelete={() =>
                    aplicarFiltros({ perfilSlug: 'todos', fontes: fontesSelecionadas, faixaSlug })
                  }
                  sx={{ fontWeight: 600, borderRadius: 999 }}
                />
              )}
              {fontesSelecionadas.map((chave) => (
                <Chip
                  key={chave}
                  label={fonteLabel(chave)}
                  onDelete={() =>
                    aplicarFiltros({
                      perfilSlug: perfilSelecionado?.slug ?? 'todos',
                      fontes: fontesSelecionadas.filter((c) => c !== chave),
                      faixaSlug,
                    })
                  }
                  sx={{ fontWeight: 600, borderRadius: 999 }}
                />
              ))}
              {faixaAtiva && (
                <Chip
                  label={faixaAtiva.label}
                  onDelete={() =>
                    aplicarFiltros({
                      perfilSlug: perfilSelecionado?.slug ?? 'todos',
                      fontes: fontesSelecionadas,
                      faixaSlug: 'todas',
                    })
                  }
                  sx={{ fontWeight: 600, borderRadius: 999 }}
                />
              )}
            </Stack>
          )}

          <Divider sx={{ borderColor: brandTheme.palette.borderSoft }} />

          {erro && <Alert severity="warning">{erro}</Alert>}

          {!erro && !carregando && produtos.length === 0 && (
            <Alert severity="info">
              Ainda não temos sugestões nessa combinação. Toque em <strong>Filtros</strong> e
              experimente outra faixa de preço. 😉
            </Alert>
          )}

          {/* Contagem + filtro por texto livre na mesma linha */}
          <Stack direction="row" spacing={1} alignItems="center">
            {total > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flexShrink: 0, whiteSpace: 'nowrap', fontWeight: 600 }}
              >
                {total} {total === 1 ? 'item' : 'itens'}
              </Typography>
            )}
            <TextField
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por palavra"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
                endAdornment: busca ? (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label="Limpar busca" onClick={() => setBusca('')}>
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 999 } }}
            />
          </Stack>

          {/* Grid de produtos */}
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
            }}
            gap={{ xs: 1.25, md: 1.75 }}
          >
            {produtos.map((produto) => (
              <Card
                key={produto.id}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${brandTheme.palette.borderSoft}`,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  backgroundColor: 'rgba(255,255,255,0.92)',
                }}
              >
                <Box
                  component="a"
                  href={produto.urlAfiliado}
                  target="_blank"
                  rel="sponsored nofollow noopener"
                  onClick={() =>
                    trackClick('ideias_produto_click', {
                      produtoId: produto.id,
                      perfil: perfilSelecionado.slug,
                      parceiro: produto.parceiro ?? undefined,
                    })
                  }
                  sx={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      backgroundColor: '#fff',
                      display: 'grid',
                      placeItems: 'center',
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    {produto.imagemUrl ? (
                      <Box
                        component="img"
                        src={produto.imagemUrl}
                        alt={produto.nome}
                        loading="lazy"
                        sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1 }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '2.5rem' }}>🎁</Typography>
                    )}
                    {produto.precoOriginal && produto.precoOriginal > produto.preco && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 6,
                          left: 6,
                          bgcolor: '#2e7d32',
                          color: '#fff',
                          borderRadius: 1.5,
                          px: 0.75,
                          py: 0.2,
                          fontSize: '0.7rem',
                          fontWeight: 800,
                          lineHeight: 1.4,
                        }}
                      >
                        -{Math.round((1 - produto.preco / produto.precoOriginal) * 100)}%
                      </Box>
                    )}
                  </Box>
                  <CardContent sx={{ p: { xs: 1.25, md: 1.5 }, pb: { xs: 1, md: 1.25 } }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.82rem', md: '0.9rem' },
                        fontWeight: 600,
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        minHeight: { xs: '2.15rem', md: '2.35rem' },
                      }}
                    >
                      {produto.nome}
                    </Typography>
                    {produto.precoOriginal && produto.precoOriginal > produto.preco && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textDecoration: 'line-through', display: 'block', mt: 0.5, lineHeight: 1 }}
                      >
                        {formatarPreco(produto.precoOriginal)}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: brandTheme.palette.primaryMain,
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        mt: produto.precoOriginal && produto.precoOriginal > produto.preco ? 0 : 0.5,
                      }}
                    >
                      {formatarPreco(produto.preco)}
                    </Typography>
                    {produto.parceiro && (
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        em {produto.parceiro}
                      </Typography>
                    )}
                  </CardContent>
                </Box>
                <Box px={{ xs: 1.25, md: 1.5 }} pb={{ xs: 1.25, md: 1.5 }} mt="auto">
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    endIcon={<OpenInNew sx={{ fontSize: 15 }} />}
                    component="a"
                    href={produto.urlAfiliado}
                    target="_blank"
                    rel="sponsored nofollow noopener"
                    onClick={() =>
                      trackClick('ideias_produto_cta', {
                        produtoId: produto.id,
                        perfil: perfilSelecionado.slug,
                        parceiro: produto.parceiro ?? undefined,
                      })
                    }
                    sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
                  >
                    Ver oferta
                  </Button>
                </Box>
              </Card>
            ))}

            {carregando &&
              Array.from({ length: produtos.length === 0 ? 8 : 4 }).map((_, index) => (
                <Card
                  key={`skeleton-${index}`}
                  elevation={0}
                  sx={{ borderRadius: 3, border: `1px solid ${brandTheme.palette.borderSoft}` }}
                >
                  <Skeleton variant="rectangular" sx={{ aspectRatio: '1 / 1', width: '100%', height: 'auto' }} />
                  <Box p={1.5}>
                    <Skeleton width="90%" />
                    <Skeleton width="55%" />
                  </Box>
                </Card>
              ))}
          </Box>

          {/* Sentinela do infinite scroll */}
          {temMais && <Box ref={sentinelaRef} sx={{ height: 1 }} />}
          {carregando && produtos.length > 0 && (
            <CircularProgress size={28} sx={{ alignSelf: 'center' }} />
          )}

          {/* Fim da lista: convida a explorar outra categoria via painel de filtros */}
          {!temMais && !carregando && produtos.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                pt: 2.5,
                borderTop: `1px dashed ${brandTheme.palette.borderSoft}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.25rem' } }}>
                🎉 Chegamos ao fim das ideias de {perfilSelecionado.emoji} {perfilSelecionado.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Quer ver presentes para outra pessoa? Ajuste os filtros.
              </Typography>
              <Button
                variant="outlined"
                onClick={abrirFiltros}
                startIcon={<Tune />}
                sx={{ borderRadius: 999, fontWeight: 700, textTransform: 'none', px: 2.5 }}
              >
                Abrir filtros
              </Button>
            </Box>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.4, px: 0.25, mt: 1, opacity: 0.75 }}
          >
            Preços e disponibilidade são atualizados diariamente e podem variar — o valor final é
            sempre o exibido no site do parceiro ao clicar.
          </Typography>

          {/* Botão flutuante de filtros (FAB) — sempre acessível ao rolar a lista */}
          <Badge
            badgeContent={filtrosAtivosCount}
            overlap="rectangular"
            sx={{
              position: 'fixed',
              bottom: { xs: 84, md: 96 },
              right: { xs: 16, md: 28 },
              zIndex: (t) => t.zIndex.speedDial,
              '& .MuiBadge-badge': {
                bgcolor: brandTheme.palette.textPrimary,
                color: '#fff',
                fontWeight: 800,
              },
            }}
          >
            <Fab
              color="primary"
              variant="extended"
              onClick={abrirFiltros}
              aria-label="Abrir filtros"
              sx={{
                fontWeight: 800,
                textTransform: 'none',
                px: 2.5,
                boxShadow: `0 10px 24px ${brandTheme.palette.heroGlow}`,
              }}
            >
              <Tune sx={{ mr: 1 }} />
              Filtros
            </Fab>
          </Badge>
        </Box>
      )}

      {renderPainelFiltros()}
    </Box>
  );
};

export default IdeiasPresentesPage;
